import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import { Container } from 'react-bootstrap';

import { setRuntimeVariable } from '../../actions/runtime';
import { openHeaderModal } from '../../actions/siteadmin/modalActions';
import { addOrderAction } from '../../actions/order/addOrderAction';
import Link from '../../components/Link/Link';
import { AngleLeftArrowIcon } from '../../components/Common/Assets/SVGIcon/CommonSvgIcon';
import Loader from '../../components/Common/Loader/Loader';
import QuestionnaireFields from '../../components/BookingDetail/OrderDetails/QuestionnaireFields';
import BookingReviewImageUpload from '../../components/BookingDetail/BookingReviewImageUpload/BookingReviewImageUpload';
import PrefferedProviderDetails from '../../components/PreferredProviderForm/PrefferedProviderDetails/PrefferedProviderDetails';
import useQueryResponse from '../../lib/hooks/useQueryResponse';
import getGuestOrderPreview from './getGuestOrderPreview.graphql';
import getGuestServiceProviders from './getGuestServiceProviders.graphql';
import { readGuestCart, writeGuestCart, writeGuestMeta, readGuestPhotos, writeGuestPhotos } from '../../helpers/bookingDraft';
import { api, subCategoryUploadDir, reviewImageUploadDir } from '../../config';
import messages from '../../locale/messages';
import bt from '../../components/commonStyle.css';
import s from './GuestReview.css';

// LOGGED-OUT guest review screen (approach B). The visitor fills EVERYTHING here — describe the job,
// pick when, pick how to get matched — while seeing the REAL server-computed price (getGuestOrderPreview,
// which reuses the same pricing helpers as the authenticated getOrder). No Order row, no charge, no
// account yet. Account + card + the actual booking fire only on the final CTA (wired next).
const money = (n, cur) => {
    if (n == null || isNaN(Number(n))) return '';
    const sym = cur === 'USD' || !cur ? '$' : '';
    return `${sym}${Number(n).toFixed(2)}${sym ? '' : ' ' + cur}`;
};

const GuestReview = ({ cartFromForm, selectedPartnerId, intl: { formatMessage } }) => {
    // Prefer the live redux cart (client-side nav from /category); fall back to the stashed guest cart
    // (a hard reload / post-login return). Always re-stash so a refresh keeps the request.
    const cart = useMemo(() => {
        const c = (Array.isArray(cartFromForm) && cartFromForm.length > 0) ? cartFromForm : readGuestCart();
        return Array.isArray(c) ? c : [];
    }, [cartFromForm]);

    useEffect(() => { if (cart.length > 0) writeGuestCart(cart); }, [cart]);

    const [description, setDescription] = useState('');
    const [when, setWhen] = useState('asap'); // 'asap' | 'schedule'
    const [scheduleAt, setScheduleAt] = useState('');
    const [mode, setMode] = useState('choose'); // 'choose' (Choose for me) | 'preferred' (Pick a pro)
    const [photos, setPhotos] = useState(() => readGuestPhotos()); // uploaded job-photo fileNames

    // A guest-uploaded file returns its fileName from the api endpoint (no auth needed there). Stash it;
    // it is LINKED to the order after login (addOrderAction). Cap at 6.
    const handlePhotoUploaded = (fileName) => {
        if (!fileName) return;
        setPhotos((prev) => {
            if (prev.length >= 6 || prev.includes(fileName)) return prev;
            const next = [...prev, fileName];
            writeGuestPhotos(next);
            return next;
        });
    };
    const removePhoto = (fileName) => {
        setPhotos((prev) => {
            const next = prev.filter((x) => x !== fileName);
            writeGuestPhotos(next);
            return next;
        });
    };

    const dropLat = cart[0]?.dropLat;
    const dropLng = cart[0]?.dropLng;
    const dropLocation = cart[0]?.dropLocation;
    const currency = cart[0]?.currency || 'USD';

    const cartList = useMemo(() => JSON.stringify(cart.map((it) => ({
        categoryId: it.categoryId,
        subCategoryId: it.subCategoryId,
        minimumHours: it.minimumHours,
        totalQuantity: it.totalQuantity,
        quantity: it.quantity,
    }))), [cart]);

    const { data, loading } = useQueryResponse({
        query: getGuestOrderPreview,
        variables: { cartList, dropLat, dropLng, requestCurrency: currency },
        skip: cart.length === 0 || dropLat == null,
        fetchPolicy: 'network-only',
        ssr: false,
    });
    const price = data?.getGuestOrderPreview?.result;

    // "Let me pick a pro" — fetch the eligible workers WITH their own prices (guest variant of
    // getServiceProvider). Only runs in that mode. Reuses the app card component below.
    const scheduledIso = when === 'schedule' && scheduleAt ? new Date(scheduleAt).toISOString() : null;
    const estimatedHours = Number(cart[0]?.minimumHours) || Number(cart[0]?.multiplierValue) || 1;
    const { data: provData, loading: provLoading } = useQueryResponse({
        query: getGuestServiceProviders,
        variables: { cartList, dropLat, dropLng, scheduledAt: scheduledIso, estimatedHours, requestCurrency: currency },
        skip: mode !== 'preferred' || cart.length === 0 || dropLat == null,
        fetchPolicy: 'network-only',
        ssr: false,
    });
    const providers = provData?.getGuestServiceProviders?.result || [];
    const selectedPro = providers.find((p) => String(p.id) === String(selectedPartnerId));

    // The total the guest sees: their chosen pro's price when picking a pro, else the admin (broadcast)
    // price from the preview.
    const displayTotal = (mode === 'preferred' && selectedPro && selectedPro.price != null)
        ? selectedPro.price
        : (price ? price.userPayableFare : null);

    const dispatch = useDispatch();
    const isAuthenticated = useSelector((st) => st.runtime?.isAuthenticated);
    // QuestionnaireFields mirrors the answers into OrderDetails.questionnaire (JSON string).
    const questionnaireValue = useSelector((st) => formValueSelector('OrderDetails')(st, 'questionnaire'));
    const canBook = cart.length > 0 && dropLat != null && !loading && !!price
        && !(mode === 'preferred' && !selectedPartnerId);

    const handleBook = () => {
        if (!canBook) return;
        // Stash the guest's choices (description / schedule / matching) — re-applied on /order after
        // login. Choose-for-me → no preferred pro (broadcast); Preferred → the pro picked above.
        const scheduledMs = (when === 'schedule' && scheduleAt) ? new Date(scheduleAt).getTime() : null;
        writeGuestMeta({
            description: description || '',
            questionnaire: questionnaireValue || null,
            scheduledAt: scheduledMs,
            estimatedHours,
            mode,
            preferredPartnerId: mode === 'preferred' ? (selectedPartnerId || null) : null,
        });
        writeGuestCart(cart);

        if (isAuthenticated) {
            // Already logged in (edge case): create the order now; addOrderAction routes to /order/:id.
            dispatch(addOrderAction(cart));
            return;
        }
        // Guest: open the login/signup gate — the existing modal handles BOTH cases (new → OTP +
        // RegisterForm; returning → OTP). referUrl=/order/replay → after auth GuestCartReplay creates
        // the real order and lands on /order/:id, where the user adds a card and confirms (existing
        // money-path: hold → dispatch). No charge happens before that final confirm.
        dispatch(setRuntimeVariable({ name: 'referUrl', value: '/order/replay' }));
        dispatch(openHeaderModal('loginModal'));
    };

    if (cart.length === 0) {
        return (
            <div className={'subPageContainer'}>
                <Container>
                    <div className={s.emptyBox}>
                        <div className={cx0(bt.commonViewBookingHeading, bt.space2)}>Your request is empty</div>
                        <Link to={'/category'} className={cx0(bt.btnPrimary, bt.cursorPointer)}>Start a booking</Link>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className={'subPageContainer'}>
            <Container>
                <div className={cx0(bt.space3, s.topLink)}>
                    <Link to={'/category'} className={cx0(bt.customLink, 'customLinkRtl')}>
                        <AngleLeftArrowIcon />
                        <FormattedMessage {...messages.goBackText} />
                    </Link>
                </div>

                <div className={s.grid}>
                    {/* LEFT — the request */}
                    <div className={s.left}>
                        <h1 className={s.heading}>Review your request</h1>

                        <div className={s.card}>
                            {cart.map((it, i) => (
                                <div key={i} className={s.lineItem}>
                                    {it.image ? (
                                        <div className={s.thumb} style={{ backgroundImage: `url(${api.apiEndpoint}${subCategoryUploadDir}medium_${it.image})` }} />
                                    ) : <div className={s.thumbBlank} />}
                                    <div className={s.lineInfo}>
                                        <div className={s.lineName}>{it.name || 'Service'}</div>
                                        <div className={s.lineMeta}>
                                            {it.pricingType === 'fixed'
                                                ? `Qty ${it.quantity || it.totalQuantity || 1}`
                                                : `${it.minimumHours || it.multiplierValue || 1} hr min`}
                                        </div>
                                    </div>
                                    <div className={s.linePrice}>{money(it.basePrice, currency)}{it.pricingType === 'fixed' ? ' /item' : ' /hr'}</div>
                                </div>
                            ))}
                        </div>

                        <div className={s.card}>
                            <div className={s.blockLabel}>Location</div>
                            <div className={s.blockValue}>{dropLocation || '—'}</div>
                        </div>

                        <div className={s.card}>
                            <label className={s.blockLabel} htmlFor="gr-desc">Describe your job</label>
                            <textarea
                                id="gr-desc"
                                className={s.textarea}
                                placeholder="Tell the pro what you need done — size, access, anything helpful."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        {/* Category questionnaire (app parity) — same component & data as the /order review.
                            Answers land in the OrderDetails 'questionnaire' redux field, carried to the booking. */}
                        {cart[0]?.categoryId && (
                            <div className={s.card}>
                                <QuestionnaireFields categoryId={cart[0].categoryId} />
                            </div>
                        )}

                        <div className={s.card}>
                            <div className={s.blockLabel}>Add photos <span className={s.optional}>(optional)</span></div>
                            <div className={s.photoRow}>
                                {photos.map((fn, i) => (
                                    <div key={fn + i} className={s.photoThumb} style={{ backgroundImage: `url(${api.apiEndpoint}${reviewImageUploadDir}medium_${fn})` }}>
                                        <button type="button" className={s.photoRemove} onClick={() => removePhoto(fn)} aria-label="Remove photo">×</button>
                                    </div>
                                ))}
                                {typeof window !== 'undefined' && photos.length < 6 && (
                                    <BookingReviewImageUpload
                                        postUrl={api.apiEndpoint + '/uploadReviewPhoto'}
                                        loaderName={'guestReviewImageLoader'}
                                        fieldName={'guestPhoto'}
                                        formName={'GuestPhotos'}
                                        reviewImages={photos}
                                        handleDropzone={handlePhotoUploaded}
                                        setImageLoader={() => {}}
                                        iconClass={s.photoPlusIcon}
                                        className={s.photoAddBox}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={s.card}>
                            <div className={s.blockLabel}>When do you need it?</div>
                            <div className={s.toggleRow}>
                                <button type="button" className={cx0(s.toggle, when === 'asap' && s.toggleOn)} onClick={() => setWhen('asap')}>As soon as possible</button>
                                <button type="button" className={cx0(s.toggle, when === 'schedule' && s.toggleOn)} onClick={() => setWhen('schedule')}>Pick a date &amp; time</button>
                            </div>
                            {when === 'schedule' && (
                                <input type="datetime-local" className={s.dateInput} value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
                            )}
                        </div>

                        <div className={s.card}>
                            <div className={s.blockLabel}>How would you like to get matched?</div>
                            <div className={s.toggleRow}>
                                <button type="button" className={cx0(s.toggle, mode === 'choose' && s.toggleOn)} onClick={() => setMode('choose')}>Choose a pro for me</button>
                                <button type="button" className={cx0(s.toggle, mode === 'preferred' && s.toggleOn)} onClick={() => setMode('preferred')}>Let me pick a pro</button>
                            </div>

                            {mode === 'preferred' && (
                                <div className={s.providerList}>
                                    {provLoading ? (
                                        <div className={s.providerLoading}><Loader type="circle" show /></div>
                                    ) : providers.length > 0 ? (
                                        <div className={'preferredProviderCardWrapper'}>
                                            {providers.map((p, i) => (
                                                <PrefferedProviderDetails providerData={p} key={p.id || i} />
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={s.muted}>No pros available for this service in your area right now. Try “Choose a pro for me”.</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT — price + CTA */}
                    <div className={s.right}>
                        <div className={s.priceCard}>
                            <div className={s.priceTitle}>Price</div>
                            {loading ? <div className={s.muted}>Calculating…</div> : price ? (
                                <>
                                    <div className={s.priceRow}><span>Subtotal</span><span>{money(price.subTotal, currency)}</span></div>
                                    <div className={s.priceRow}><span>Service fee</span><span>{money(price.userServiceFee, currency)}</span></div>
                                    <div className={s.priceRow}><span>Visit fee</span><span>{money(price.travellingPrice, currency)}</span></div>
                                    <div className={s.priceDivider} />
                                    <div className={cx0(s.priceRow, s.priceTotal)}><span>Total</span><span>{money(displayTotal, currency)}</span></div>
                                    {mode === 'preferred' && selectedPro && (
                                        <div className={s.muted}>Price for {selectedPro.firstName || 'your selected pro'}.</div>
                                    )}
                                    <div className={s.muted}>You won’t be charged until you confirm.</div>
                                </>
                            ) : <div className={s.muted}>Add a service to see your price.</div>}

                            <button
                                type="button"
                                className={cx0(bt.btnPrimary, s.cta, !canBook && s.ctaDisabled)}
                                onClick={handleBook}
                                disabled={!canBook}
                            >
                                Continue to book →
                            </button>
                            <div className={s.ctaNote}>
                                {mode === 'preferred' && !selectedPartnerId
                                    ? 'Select a pro above to continue.'
                                    : 'Next: log in or create your account, add your card, and we start finding your pro.'}
                            </div>
                        </div>
                    </div>
                </div>
            </Container>
        </div>
    );
};

// tiny local classnames joiner (avoids adding a dependency import just for this screen)
function cx0(...args) { return args.filter(Boolean).join(' '); }

const selector = formValueSelector('BookingForm');
const mapState = (state) => ({
    cartFromForm: selector(state, 'orderData'),
    // Selected preferred pro id — the same redux field the reused app card (PrefferedProviderDetails) writes.
    selectedPartnerId: state.form?.PreferredProviderForm?.values?.preferredPartnerId,
});

export default injectIntl(withStyles(bt, s)(connect(mapState)(GuestReview)));
